import { Link } from "react-router-dom";
import { ChevronRight, Mail, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import { GRIEVANCE, LEGAL_DISCLAIMER, LEGAL_META } from "@/legal/documents";

const GrievanceBlock = () => (
  <div
    className="mt-5 rounded-xl border border-white/[0.1] bg-white/[0.02] p-5"
    data-testid="grievance-block"
  >
    <dl className="flex flex-col gap-2 text-sm">
      <div className="flex gap-2">
        <dt className="w-32 shrink-0 text-muted-foreground">Grievance Officer:</dt>
        <dd className="font-medium">{GRIEVANCE.officer}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="w-32 shrink-0 text-muted-foreground">Designation:</dt>
        <dd>{GRIEVANCE.designation}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="w-32 shrink-0 text-muted-foreground">Email:</dt>
        <dd>
          <a href={`mailto:${GRIEVANCE.email}`} className="text-primary underline-offset-4 hover:underline">
            {GRIEVANCE.email}
          </a>
        </dd>
      </div>
      <div className="flex gap-2">
        <dt className="w-32 shrink-0 text-muted-foreground">Address:</dt>
        <dd>{GRIEVANCE.address}</dd>
      </div>
    </dl>
  </div>
);

export default function LegalPage({ doc }) {
  return (
    <div className="vault-container max-w-3xl pt-14 sm:pt-16" data-testid={`legal-page-${doc.slug}`}>
      <Reveal>
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="legal-breadcrumb">
          <Link to="/" className="transition-colors duration-200 hover:text-foreground">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{doc.title}</span>
        </nav>
        <p className="overline-label mt-8">Legal</p>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tighter sm:text-4xl">
          {doc.title}
        </h1>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="legal-updated">
          Effective date: {LEGAL_META.effective} · Last updated: {LEGAL_META.effective}
        </p>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">{doc.intro}</p>

        <div className="mt-10 rounded-2xl border border-white/[0.08] bg-card p-6" data-testid="legal-toc">
          <p className="overline-label mb-4">On this page</p>
          <ol className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {doc.sections.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`toc-${s.id}`}
                  className="group flex items-baseline gap-2.5 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  <span className="font-mono text-[10px] text-primary/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="underline-offset-4 group-hover:underline">{s.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </Reveal>

      <div className="mt-14 flex flex-col gap-12">
        {doc.sections.map((s, i) => (
          <Reveal key={s.id}>
            <section id={s.id} className="scroll-mt-24" data-testid={`legal-section-${s.id}`}>
              <div className="flex items-baseline gap-3.5">
                <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-xl font-semibold tracking-tight">{s.title}</h2>
              </div>
              {s.paragraphs?.map((p, j) => (
                <p key={j} className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="mt-4 flex flex-col gap-2.5">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                      <span className="mt-[7px] block h-1 w-1 shrink-0 rotate-45 bg-primary/70" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              {s.after && (
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{s.after}</p>
              )}
              {s.grievanceBlock && <GrievanceBlock />}
            </section>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div
          className="mt-16 rounded-2xl border border-white/[0.08] bg-card p-7"
          data-testid="legal-support-block"
        >
          <p className="overline-label">Questions about this policy</p>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            <a
              href={`mailto:${LEGAL_META.email}`}
              className="flex items-center gap-2.5 text-foreground transition-colors duration-200 hover:text-primary"
            >
              <Mail className="h-4 w-4 text-primary" /> {LEGAL_META.email}
            </a>
            <p className="flex items-start gap-2.5 text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              {LEGAL_META.controller}, {LEGAL_META.address}
            </p>
          </div>
        </div>
        <p
          className="mt-8 rounded-xl border border-dashed border-white/[0.12] px-5 py-4 text-xs leading-relaxed text-muted-foreground/70"
          data-testid="legal-disclaimer"
        >
          {LEGAL_DISCLAIMER}
        </p>
      </Reveal>
    </div>
  );
}
